/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../components/customAuth/services/useAuth';
import { SiteLink } from '../../../components';
import env from '../../../env';
import styles from './style';

export function MyAccount(props) {
    const auth = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);

    // Fetch user profile on mount
    useEffect(() => {
        const fetchProfile = async () => {
            if (!auth.user) {
                setLoading(false);
                return;
            }

            try {
                const token = await auth.user.getIdToken();
                const response = await fetch(
                    env.apiBase + `/api/Users/GetUserByEmail/${encodeURIComponent(auth.user.email)}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch profile');
                }

                const data = await response.json();
                const profile = data.results?.userinfo;
                
                if (profile) {
                    setUserProfile(profile);
                    setFirstName(profile.firstName || '');
                    setLastName(profile.lastName || '');
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                setErrorMessage('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [auth.user]);

    const handleSave = async () => {
        setSaving(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const token = await auth.user.getIdToken();
            const response = await fetch(env.apiBase + '/api/Users/UpdateMyProfile', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    firstName,
                    lastName
                })
            });

            const data = await response.json();

            if (data.errors && data.errors.length > 0) {
                setErrorMessage(data.errors[0].errorMessage || 'Failed to update profile');
            } else {
                setSuccessMessage('Profile updated successfully!');
                // Update local state
                setUserProfile(prev => ({
                    ...prev,
                    firstName,
                    lastName,
                    displayName: `${firstName} ${lastName}`.trim()
                }));
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            setErrorMessage('Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateMemberDuration = (dateCreated) => {
        if (!dateCreated) return 'N/A';
        const start = new Date(dateCreated);
        const now = new Date();
        const diffTime = Math.abs(now - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 30) {
            return `${diffDays} days`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return `${months} month${months > 1 ? 's' : ''}`;
        } else {
            const years = Math.floor(diffDays / 365);
            const remainingMonths = Math.floor((diffDays % 365) / 30);
            if (remainingMonths > 0) {
                return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
            }
            return `${years} year${years > 1 ? 's' : ''}`;
        }
    };

    const getInitials = () => {
        if (firstName && lastName) {
            return `${firstName[0]}${lastName[0]}`.toUpperCase();
        }
        if (userProfile?.email) {
            return userProfile.email[0].toUpperCase();
        }
        return '?';
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setErrorMessage('Invalid file type. Please upload an image (JPG, PNG, GIF, or WebP).');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage('File too large. Maximum size is 5MB.');
            return;
        }

        setUploading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const token = await auth.user.getIdToken();
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(env.apiBase + '/api/Users/UploadProfilePicture', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();

            if (data.errors && data.errors.length > 0) {
                setErrorMessage(data.errors[0].errorMessage || 'Failed to upload image');
            } else {
                const newPhotoUrl = data.results?.photoUrl;
                if (newPhotoUrl) {
                    setUserProfile(prev => ({
                        ...prev,
                        photoUrl: newPhotoUrl
                    }));
                    setSuccessMessage('Profile picture updated successfully!');
                }
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            setErrorMessage('Failed to upload image');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Not logged in
    if (!auth.user && !loading) {
        return (
            <div css={styles.container}>
                <div css={styles.loginPrompt}>
                    <h2>Please log in to view your account</h2>
                    <p>
                        <SiteLink to="/login">Click here to log in</SiteLink>
                    </p>
                </div>
            </div>
        );
    }

    // Loading
    if (loading) {
        return (
            <div css={styles.container}>
                <div css={styles.loading}>Loading your account...</div>
            </div>
        );
    }

    return (
        <div css={styles.container}>
            <h1>My Account</h1>

            {/* Account Section */}
            <div css={styles.section}>
                <div css={styles.sectionHeader}>Account</div>
                <div css={styles.sectionContent}>
                    <div css={styles.row}>
                        <span css={styles.label}>Username</span>
                        <span css={styles.value}>{userProfile?.email || auth.user?.email}</span>
                    </div>
                    <div css={styles.row}>
                        <span css={styles.label}>Member Type</span>
                        <span css={styles.value}>
                            {userProfile?.memberType === 99 ? 'Administrator' : 'Member'}
                        </span>
                    </div>
                    <div css={styles.row}>
                        <span css={styles.label}>Status</span>
                        <span css={styles.value}>
                            {userProfile?.profileStatus === 0 ? (
                                <span style={{ color: '#28a745' }}>Active</span>
                            ) : (
                                <span style={{ color: '#dc3545' }}>Inactive</span>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            {/* History Section */}
            <div css={styles.section}>
                <div css={styles.sectionHeader}>History</div>
                <div css={styles.sectionContent}>
                    <div css={styles.historyTimeline}>
                        <div>
                            <div css={styles.timelineDot}></div>
                        </div>
                        <div css={styles.timelineLabel}>
                            <strong>Account Created</strong><br />
                            {formatDate(userProfile?.dateCreated)}
                        </div>
                        <div css={styles.timelineLine}></div>
                        <div>
                            <div css={styles.timelineDot}></div>
                        </div>
                        <div css={styles.timelineLabel}>
                            <strong>Today</strong><br />
                            {formatDate(new Date())}
                        </div>
                    </div>
                    <div css={styles.row}>
                        <span css={styles.label}>Member for</span>
                        <span css={styles.value}>{calculateMemberDuration(userProfile?.dateCreated)}</span>
                    </div>
                </div>
            </div>

            {/* Edit Section */}
            <div css={styles.section}>
                <div css={styles.sectionHeader}>Edit Profile</div>
                <div css={styles.sectionContent}>
                    {successMessage && <div css={styles.successMessage}>{successMessage}</div>}
                    {errorMessage && <div css={styles.errorMessage}>{errorMessage}</div>}

                    {/* Avatar */}
                    <div css={styles.avatarSection}>
                        {userProfile?.photoUrl ? (
                            <img src={env.apiBase + userProfile.photoUrl} alt="Profile" css={styles.avatar} />
                        ) : (
                            <div css={styles.avatarPlaceholder}>
                                {getInitials()}
                            </div>
                        )}
                        <div>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>Profile Picture</p>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                style={{ display: 'none' }}
                            />
                            <button
                                css={styles.uploadButton}
                                onClick={handleUploadClick}
                                disabled={uploading}
                            >
                                {uploading ? 'Uploading...' : 'Upload Photo'}
                            </button>
                            <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                                JPG, PNG, GIF or WebP. Max 5MB.
                            </p>
                        </div>
                    </div>

                    {/* Name Fields */}
                    <div css={styles.formRow}>
                        <div css={[styles.formGroup, styles.formCol]}>
                            <label htmlFor="firstName">First Name</label>
                            <input
                                id="firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Enter your first name"
                            />
                        </div>
                        <div css={[styles.formGroup, styles.formCol]}>
                            <label htmlFor="lastName">Last Name</label>
                            <input
                                id="lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Enter your last name"
                            />
                        </div>
                    </div>

                    {/* Buttons */}
                    <div css={styles.buttonGroup}>
                        <button
                            css={styles.primaryButton}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <SiteLink to="/forgot-password" css={styles.secondaryButton}>
                            Reset Password
                        </SiteLink>
                    </div>
                </div>
            </div>
        </div>
    );
}
